/**
 * @file Payment-route value set (AGENCY / DIRECT_UNTRACKED / DIRECT_TRACKED) and the
 * module-level helpers that resolve, reduce, gate and label a charge or charge
 * template's `paymentRouteId`. Sole home of the route ids — no literal escapes it.
 *
 * Flow: backend payload → `getPaymentRouteIdForChargeData` (ChargeFactory only) →
 * entity `paymentRouteId` → components read it through `isDirectPaymentRoute` and
 * `getSharedPaymentRouteForChargeTemplates`; `PAYMENT_ROUTE_OPTIONS` is the ordered
 * per-route table (radio option key with its `:partyName` param, and which party —
 * agency or owner — the option names) that `PaymentRouteRadio` renders.
 * Exceptions: none — every helper is total over its inputs; an absent route reads
 * as AGENCY / not-direct, and a legacy boolean payload never resolves to
 * DIRECT_TRACKED.
 */

export type PaymentRouteId = 1 | 2 | 3;

export const PAYMENT_ROUTE: {
  AGENCY: 1;
  DIRECT_UNTRACKED: 2;
  DIRECT_TRACKED: 3;
} = {
  AGENCY: 1,
  DIRECT_UNTRACKED: 2,
  DIRECT_TRACKED: 3,
};

export interface PaymentRouteOption {
  id: PaymentRouteId;
  optionKey: string;
  checkingParty: "agency" | "owner";
}

export const PAYMENT_ROUTE_OPTIONS: PaymentRouteOption[] = [
  { id: PAYMENT_ROUTE.AGENCY, optionKey: "label-route-option-agency", checkingParty: "agency" },
  { id: PAYMENT_ROUTE.DIRECT_UNTRACKED, optionKey: "label-route-option-direct-untracked", checkingParty: "owner" },
  { id: PAYMENT_ROUTE.DIRECT_TRACKED, optionKey: "label-route-option-direct-tracked", checkingParty: "agency" },
];

export function isPaymentRouteId(value: any): value is PaymentRouteId {
  return PAYMENT_ROUTE_OPTIONS.some(option => option.id === value);
}

export function getPaymentRouteIdForChargeData(
  data: { paymentRouteId?: any; isPaidDirectlyToOwner?: any },
): PaymentRouteId {
  if (isPaymentRouteId(data.paymentRouteId)) {
    return data.paymentRouteId;
  }
  if (typeof data.isPaidDirectlyToOwner === "boolean" && data.isPaidDirectlyToOwner === true) {
    return PAYMENT_ROUTE.DIRECT_UNTRACKED;
  }
  if (typeof data.isPaidDirectlyToOwner === "boolean" && data.isPaidDirectlyToOwner === false) {
    return PAYMENT_ROUTE.AGENCY;
  }
  return PAYMENT_ROUTE.AGENCY;
}

export function getSharedPaymentRouteForChargeTemplates(
  chargeTemplates?: { paymentRouteId: PaymentRouteId }[],
): PaymentRouteId {
  if (!chargeTemplates || chargeTemplates.length === 0) {
    return PAYMENT_ROUTE.AGENCY;
  }
  const firstRoute = chargeTemplates[0].paymentRouteId;
  if (chargeTemplates.every(chargeTemplate => chargeTemplate.paymentRouteId === firstRoute)) {
    return isPaymentRouteId(firstRoute) ? firstRoute : PAYMENT_ROUTE.AGENCY;
  }
  return PAYMENT_ROUTE.AGENCY;
}

export function isDirectPaymentRoute(paymentRouteId?: PaymentRouteId): boolean {
  return paymentRouteId === PAYMENT_ROUTE.DIRECT_UNTRACKED
    || paymentRouteId === PAYMENT_ROUTE.DIRECT_TRACKED;
}
